import React, { useEffect, useState } from "react";
import {
  FaBoxOpen,
  FaCheckCircle,
  FaClock,
  FaTruck,
  FaChevronDown,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { getAllOrdersApi } from "../services/api";
import { useAuthStore } from "../store/authStore";
import Layout from "./Layout";
import ProfileSidebar from "./ProfileSidebar";
import OrderDetails from "./OrderDetails";

export default function AllOrders() {
  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  // =====================================================
  // ADMIN CHECK
  // =====================================================

  const ADMIN_ID = "6a86fc0273e1ca02be82c099";

  const isAdmin = user?.id === ADMIN_ID;

  // =====================================================
  // FETCH ALL ORDERS
  // =====================================================

  useEffect(() => {
    let isMounted = true;

    const fetchAllOrders = async () => {
      if (!isAdmin) {
        setError("You are not authorized to view all orders.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await getAllOrdersApi();

        console.log("ALL ORDERS API RESPONSE:", response);

        if (!isMounted) return;

        if (response?.success) {
          setOrders(response.orders || []);
        } else if (Array.isArray(response)) {
          setOrders(response);
        } else {
          setError(response?.message || "Failed to load all orders.");
        }
      } catch (err) {
        console.error("Fetch all orders error:", err);

        if (!isMounted) return;

        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load all orders.",
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAllOrders();

    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  // =====================================================
  // HELPERS
  // =====================================================

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN");
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "bg-green-100 text-green-700";

      case "shipped":
        return "bg-blue-100 text-blue-700";

      case "processing":
        return "bg-yellow-100 text-yellow-700";

      case "cancelled":
        return "bg-red-100 text-red-700";

      case "pending":
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return <FaCheckCircle />;

      case "shipped":
        return <FaTruck />;

      case "processing":
        return <FaClock />;

      default:
        return <FaBoxOpen />;
    }
  };

  const getCustomerName = (order) => {
    return order.shipping?.name || "Customer";
  };

  const getCustomerPhone = (order) => {
    return order.shipping?.phone || "-";
  };

  // =====================================================
  // UNAUTHORIZED
  // =====================================================

  if (!isAdmin) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>

            <p>You are not authorized to view all orders.</p>

            <button
              type="button"
              onClick={() => navigate("/orders")}
              className="mt-5 bg-light_primary hover:bg-hover_light_primary text-white px-6 py-3 rounded-lg transition"
            >
              Back to My Orders
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Layout>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <section className="bg-[#FFF8EC] px-6 md:px-16 py-8">
        <div className="mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center">
              <FaBoxOpen className="text-2xl text-[#FF945B]" />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-light_primary">
                All Orders
              </h2>

              <p className="text-gray-500 mt-1">
                Manage and view all customer orders
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <section className="px-6 lg:px-12 md:px-12 sm:px-12 xs:px-10 xxs:px-8 py-6 md:py-10 lg:py-10 sm:py-10 xs:py-8 xxs:py-6 mx-auto bg-white">
        <div className="mx-auto grid grid-cols-1 md:grid-cols-[270px_1fr] gap-8">
          {/* SIDEBAR */}

          <ProfileSidebar />

          {/* ORDER LIST */}

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm py-6 md:py-8 px-3">
            {/* LOADING */}

            {loading && (
              <div className="text-center py-20 text-gray-500">
                Loading all orders...
              </div>
            )}

            {/* ERROR */}

            {!loading && error && (
              <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 text-center">
                {error}
              </div>
            )}

            {/* NO ORDERS */}

            {!loading && !error && orders.length === 0 && (
              <div className="p-10 text-center">
                <FaBoxOpen className="mx-auto text-5xl text-gray-300 mb-4" />

                <h2 className="text-2xl font-semibold text-gray-800">
                  No Orders Found
                </h2>

                <p className="text-gray-500 mt-2">
                  There are currently no customer orders.
                </p>
              </div>
            )}

            {/* ORDERS */}

            {!loading && !error && orders.length > 0 && (
              <>
                {/* LIST HEADER */}

                <div className="flex items-center justify-between border-b border-gray-200 pb-5 mb-6">
                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900">
                      Order List
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                      Click an order to view complete details
                    </p>
                  </div>

                  <span className="bg-[#FFF3E5] text-[#E87932] px-4 py-2 rounded-full text-sm font-medium">
                    {orders.length} Orders
                  </span>
                </div>

                {/* TABLE HEADER */}

                <div className="hidden lg:grid grid-cols-[1.5fr_1.3fr_1fr_0.7fr_0.8fr_0.7fr_40px] gap-4 px-5 py-3 bg-gray-50 border border-gray-200 rounded-t-xl text-xs font-semibold text-gray-500 uppercase">
                  <div>Order</div>
                  <div>Customer</div>
                  <div>Date</div>
                  <div>Amount</div>
                  <div>Order Status</div>
                  <div>Payment Status</div>
                  <div />
                </div>

                {/* ORDER ROWS */}

                <div className="border-x border-b border-gray-200 rounded-b-xl overflow-hidden">
                  {orders.map((order) => (
                    <button
                      key={order._id}
                      type="button"
                      onClick={() => setSelectedOrder(order)}
                      className="w-full text-left grid grid-cols-1 lg:grid-cols-[1.5fr_1.3fr_1fr_0.7fr_1fr_0.7fr_40px] gap-3 lg:gap-4 px-5 py-5 border-b border-gray-200 last:border-b-0 hover:bg-[#FFF8EC] transition items-center"
                    >
                      {/* ORDER */}

                      <div>
                        <p className="text-xs text-gray-500 mb-1">Order ID</p>

                        <p className="font-semibold text-gray-800 break-all">
                          #{order._id}
                        </p>
                      </div>

                      {/* CUSTOMER */}

                      <div>
                        <p className="font-medium text-gray-800">
                          {getCustomerName(order)}
                        </p>

                        <p className="text-sm text-gray-500 truncate">
                          {getCustomerPhone(order)}
                        </p>
                      </div>

                      {/* DATE */}

                      <div>
                        <p className="text-sm text-gray-500 lg:hidden">
                          Order Date
                        </p>

                        <p className="font-medium text-gray-800">
                          {formatDate(order.createdAt)}
                        </p>
                      </div>

                      {/* AMOUNT */}

                      <div>
                        <p className="text-sm text-gray-500 lg:hidden">Total</p>

                        <p className="font-semibold text-light_primary">
                          ₹{formatAmount(order.amount)}
                        </p>
                      </div>

                      {/* STATUS */}

                      <div>
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium ${getStatusClass(
                            order.order_status,
                          )}`}
                        >
                          {getStatusIcon(order.order_status)}

                          {order.order_status || "Pending"}
                        </span>
                      </div>

                      <div>
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium ${getStatusClass(
                            order.status,
                          )}`}
                        >
                          {getStatusIcon(order.status)}

                          {order.status || "Pending"}
                        </span>
                      </div>

                      {/* ARROW */}

                      <div className="hidden lg:flex justify-end">
                        <FaChevronDown className="text-gray-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* =====================================================
          ORDER DETAILS COMPONENT
      ===================================================== */}

      {selectedOrder && (
        <OrderDetails
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </Layout>
  );
}
